import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { Address, Cart } from '@getjust/gateway';

import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { useCart, useCountriesFromCache } from '$src/hooks/queries';
import { UPSELL_QUERY_KEY } from '$src/hooks/queries/useUpsellProducts';
import { useCorrectionsAtom } from '$src/hooks/state/useCorrectionsAtom';
import { gateway, setXCountryGatewayHeader } from '$src/http';

export const UPDATE_SHIPPING_ADDRESS_MUTATION_KEY = 'MUTATION/UPDATE_SHIPPING_ADDRESS';

export const useUpdateShippingAddress = () => {
  const { initCorrections } = useCorrectionsAtom();
  const queryClient = useQueryClient();
  const baseUrl = useBaseApiUrl();
  const countries = useCountriesFromCache();
  const { data: cart } = useCart();
  const updateCartId = useCartIdUpdater();

  const mutation = useMutation({
    mutationFn: ({ address, email }: { address: Address; email: string; alreadyRetried?: boolean }) => {
      const countryCodeContext =
        (address?.countryCode && address?.countryCode.length !== 0 ? address?.countryCode : undefined) ??
        countries.find((country) => country.country === address?.country)?.value;
      return gateway.post<Cart>(`${baseUrl}/update-shipping-address`, { address, email, countryCodeContext });
    },
    retry: 2,
    mutationKey: [UPDATE_SHIPPING_ADDRESS_MUTATION_KEY, baseUrl],
    onMutate: async ({ address }: { address: Address; email: string; alreadyRetried?: boolean }) => {
      const previousCart = queryClient.getQueryData<Cart>([CART_QUERY_KEY, cart?.id]);
      const newCart = { ...previousCart, shipping: { address, ...previousCart?.shipping } };
      // @ts-expect-error - known
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, previousCart?.id], newCart);
      // Return a context object with the snapshotted value
      return { previousCart };
    },
    onSuccess: ({ data }, _, context) => {
      updateCartId(data?.id);

      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);
      if (data.corrections?.length) {
        initCorrections(data.corrections);
      }
      const countryCode = data.shipping.address?.countryCode;
      const oldCountryCode = context?.previousCart?.shipping?.address?.countryCode;

      if (countryCode) {
        setXCountryGatewayHeader(countryCode.toLocaleUpperCase());
        // Invalidate upsell products query when country changes since it affects available products/currency
        if (oldCountryCode !== countryCode) {
          queryClient.invalidateQueries({ queryKey: [UPSELL_QUERY_KEY] });
        }
      }
    },
    onError: (error: unknown, _, context) => {
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, context?.previousCart?.id], context?.previousCart);
      if (error instanceof AxiosError) {
        if (error?.response?.data?.error?.[0]?.code === 'NOT_SUPPORTED') {
          const cartWithoutShippingMethod: Cart = {
            ...(cart as Cart),
            shipping: {
              ...cart?.shipping,
              // @ts-expect-error - address1 is set for triggering unavailable message in components/Delivery/components/ShippingMethods.tsx
              address: {
                address1: 'unavailable shipping',
              },
              ready: Boolean(cart?.shipping.ready),
              methods: [],
              selected: undefined,
            },
          };
          queryClient.setQueryData<Cart>(
            [CART_QUERY_KEY, cartWithoutShippingMethod.id],
            cartWithoutShippingMethod,
          );
        }
      }
    },
  });

  return { updateShippingAddress: mutation.mutateAsync, state: mutation.status, reset: mutation.reset };
};
